import * as THREE from 'three';

/**
 * Calculate volume and surface area from a Three.js BufferGeometry
 * @param {THREE.BufferGeometry} geometry - The geometry to analyze
 * @returns {Object} Object containing volume (cm³), surface area (cm²), and bounding box
 */
export function calculateMeshMetrics(geometry) {
  if (!geometry.index) {
    geometry = geometry.toNonIndexed();
  }

  const position = geometry.attributes.position;
  const triangles = position.count / 3;

  let volume = 0;
  let surfaceArea = 0;

  // Calculate bounding box
  geometry.computeBoundingBox();
  const bbox = geometry.boundingBox;
  const bboxSize = new THREE.Vector3();
  bbox.getSize(bboxSize);

  // Iterate through triangles
  for (let i = 0; i < triangles; i++) {
    const i0 = i * 3;
    const i1 = i * 3 + 1;
    const i2 = i * 3 + 2;

    const v0 = new THREE.Vector3(
      position.getX(i0),
      position.getY(i0),
      position.getZ(i0)
    );
    const v1 = new THREE.Vector3(
      position.getX(i1),
      position.getY(i1),
      position.getZ(i1)
    );
    const v2 = new THREE.Vector3(
      position.getX(i2),
      position.getY(i2),
      position.getZ(i2)
    );

    // Calculate signed volume of tetrahedron (v0, v1, v2, origin)
    const signedVolume = v0.dot(new THREE.Vector3().crossVectors(v1, v2)) / 6.0;
    volume += signedVolume;

    // Calculate surface area of triangle
    const edge1 = new THREE.Vector3().subVectors(v1, v0);
    const edge2 = new THREE.Vector3().subVectors(v2, v0);
    const crossProduct = new THREE.Vector3().crossVectors(edge1, edge2);
    surfaceArea += crossProduct.length() / 2.0;
  }

  // Convert from mm³ to cm³ (assuming model units are mm)
  const volumeCm3 = Math.abs(volume) / 1000;
  const surfaceAreaCm2 = surfaceArea / 100;

  return {
    volume_cm3: parseFloat(volumeCm3.toFixed(2)),
    surface_cm2: parseFloat(surfaceAreaCm2.toFixed(2)),
    bbox: {
      x: parseFloat(bboxSize.x.toFixed(2)),
      y: parseFloat(bboxSize.y.toFixed(2)),
      z: parseFloat(bboxSize.z.toFixed(2))
    },
    triangle_count: triangles
  };
}

/**
 * Validate if a mesh is manifold (watertight)
 * This is a simplified check - just verifies we have a reasonable volume
 * @param {Object} metrics - Mesh metrics from calculateMeshMetrics
 * @returns {Object} Object with isValid boolean and warnings array
 */
export function validateMesh(metrics) {
  const warnings = [];

  if (metrics.volume_cm3 <= 0) {
    warnings.push('Mesh volume is zero or negative - may not be manifold');
  }

  if (metrics.triangle_count > 100000) {
    warnings.push('High triangle count may cause performance issues');
  }

  return {
    isValid: warnings.length === 0,
    warnings
  };
}
